import {
  hasProjectLevelScoping,
  POWERSCHOOL_SCHEMA_KNOWLEDGE,
  scheduleMembershipsForTable,
  transitiveInheritFromParents,
} from "@Simplicity-Education/core";
import type { Schedules, TableConfig } from "../tables/types";
import type { ScheduleAuditConnector } from "./ScheduleAuditContext";

interface ScheduleAudit {
  /** Per schedule, entry names no definition matches: `applySchedule` passes them through and the run fails. */
  undefinedScheduled: Record<string, string[]>;
  /** Defined tables no scheduled run picks up, in `vars.tables` order. */
  unscheduledTables: string[];
}

function namesOf(items: unknown): string[] {
  if (!Array.isArray(items)) return [];
  const names = items
    .map((item: unknown) => (item as { name?: unknown } | null)?.name)
    .filter((name): name is string => typeof name === "string");
  return [...new Set(names)];
}

/**
 * Lowercased tables a scoped PowerSchool connector runs as the `inheritFrom` parent of a scheduled
 * table — computed schedule by schedule with the same knowledge and gate `applySchedule` uses.
 */
function cascadeParents(
  schedules: Schedules,
  connector: ScheduleAuditConnector | null,
): Set<string> {
  const parents = new Set<string>();
  if (connector?.connectorType !== "powerschools" || !hasProjectLevelScoping(connector.vars)) {
    return parents;
  }
  for (const entries of Object.values(schedules ?? {})) {
    const pulled = transitiveInheritFromParents(namesOf(entries), POWERSCHOOL_SCHEMA_KNOWLEDGE);
    for (const name of pulled) parents.add(name);
  }
  return parents;
}

/**
 * SIM-3044: `vars.schedules` checked against `vars.tables`. Both lists come from one matcher,
 * core's `scheduleMembershipsForTable` — case-insensitive and untrimmed, exactly as
 * `applySchedule` resolves an entry — so they cannot disagree with each other or with a run.
 */
export function auditSchedules(
  tables: TableConfig[],
  schedules: Schedules,
  connector: ScheduleAuditConnector | null = null,
): ScheduleAudit {
  const vars = { schedules };
  const pulledIn = cascadeParents(schedules, connector);
  const resolved = new Set<string>();
  const unscheduledTables: string[] = [];
  // A blank name is a Tables row still being typed, not a definition.
  for (const table of namesOf(tables).filter((name) => name.trim() !== "")) {
    const memberships = scheduleMembershipsForTable(vars, table);
    if (memberships.length === 0 && !pulledIn.has(table.toLowerCase())) {
      unscheduledTables.push(table);
    }
    for (const m of memberships) for (const e of m.entries) resolved.add(e.name);
  }

  const undefinedScheduled: Record<string, string[]> = {};
  for (const [schedule, entries] of Object.entries(schedules ?? {})) {
    const missing = namesOf(entries).filter((name) => !resolved.has(name));
    if (missing.length > 0) undefinedScheduled[schedule] = missing;
  }
  return { undefinedScheduled, unscheduledTables };
}
